// meetCallChanges.js — WHAT CHANGED IN THE WORKSPACE DURING THE CALL (Nika
// 2026-10-23: "so if I catch events, tasks, some workspace additions, it can
// show up … so it automatically catches the data events - does not need to
// wait for the call end. but also shows on the end screen of the call").
//
// The call's own summary (functions/meetLiveRecap.js) catches what was SAID;
// this catches what was DONE while the call ran — the workspace's activity
// timeline (ActivityEntry: an app's records, CRM, bookings, … `created` /
// `updated`, server activityEntries) since the call began, grouped by the app
// that owns it: "Merchants · 2 added · 1 updated", a press opens the app. A
// collection no installed app claims is its own group, named for the
// collection ("tc_tasks" → "Tasks"), with nothing to open. The timeline carries
// no titles, so a group is its app (or collection) and its record counts.
//
// Read at most once a minute while the call runs (MeetCallChanges' tick, on
// the shell's 30 s tick) and once more at Leave, so the end screen has it.
// ONE root key, `_callChanges` { roomId, since, at, groups: [{ key, label,
// appKey, route, added, updated }] }; the reading's own bookkeeping lives on
// the root under a double-underscore key (never state).
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'

// When the call began: a fresh room's own birth (a call made for it — Fly's,
// the person popover's — is under six hours old), else its earliest live
// participant, else the first line the live summary heard, else an hour ago.
export function meetCallSince(root, roomId) {
  const r = root && typeof root === 'object' ? root : {}
  const now = Date.now()
  const room = (Array.isArray(r.rooms) ? r.rooms : []).find((x) => x && x.id === roomId)
  const born = room ? Date.parse(room.createdAt || '') || 0 : 0
  if (born && now - born < 6 * 3600 * 1000) return born
  const joined = ((r.liveParticipants && r.liveParticipants[roomId]) || [])
    .map((p) => Date.parse((p && p.joinedAt) || '') || 0)
    .filter((t) => t > 0)
  if (joined.length) return Math.min(...joined)
  const rec = r.__meetLiveRecap && r.__meetLiveRecap[roomId]
  if (rec && rec.firstAt) return rec.firstAt
  return now - 3600 * 1000
}

// Read the timeline since the call began and write `_callChanges`. Returns the
// groups (tests await it); a failed read keeps what was shown.
export async function meetCallChangesLoad(roomId) {
  const root = this.state && this.state.root
  if (!root || !roomId || typeof root.update !== 'function') return null
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  if (!workspaceId) return null
  const memo = (root.__meetCallChanges = root.__meetCallChanges || {})
  if (memo.busy) return null
  memo.busy = true
  memo.at = Date.now()
  try {
    const since = this.call('meetCallSince', root, roomId)
    const sdk = this.sdk && typeof this.sdk.getService === 'function' ? this.sdk : getSDK()
    const svc = await sdk.getService('activityEntries')
    const res =
      svc && typeof svc.list === 'function'
        ? await svc.list({ since: new Date(since).toISOString(), limit: 100, workspaceId })
        : null
    const rows = Array.isArray(res)
      ? res
      : (res && (res.data || res.rows || res.entries || res.items)) || []
    const groups = this.call('meetCallChangesGroup', root, rows)
    root.update({ _callChanges: { roomId, since, at: Date.now(), groups } }, { preventFetch: true })
    return groups
  } catch (err) {
    console.warn('[meet] call changes not read:', err && err.message)
    return null
  } finally {
    memo.busy = false
  }
}

// PURE: the timeline's rows → one group per app that owns them, in the order
// their first change came. A record names its app by the collection the app's
// manifest routes (recordRoutes), else by the ONE installed app whose manifest
// names that collection; a CRM entity is the app on /crm. A collection no app
// claims — a module app that declares none of its collections (measured on dev
// 2026-10-23: Thinking Creatives' tc_tasks / tc_activity) — is its own group,
// named for the collection ("tc_tasks" → "Tasks"), with no page to open.
// Counts are RECORDS, not rows: a record edited five times is one updated, and
// one made during the call is added, never also updated.
export function meetCallChangesGroup(root, rows) {
  const r = root && typeof root === 'object' ? root : {}
  const apps = (Array.isArray(r.workspaceApps) ? r.workspaceApps : [])
    .map((a) => (a && typeof a.parse === 'function' ? a.parse() : a))
    .filter((a) => a && typeof a === 'object')
  const keyOf = (v) =>
    String(v == null ? '' : v)
      .trim()
      .toLowerCase()
  // a navbar item carries its route as `route` or `path`
  const navRoute = (n) => String((n && (n.route || n.path)) || '').split('?')[0]
  const mentions = {}
  const byCollection = (key) => {
    const routed = apps.find(
      (a) =>
        Array.isArray(a.recordRoutes) && a.recordRoutes.some((rr) => rr && keyOf(rr.key) === key)
    )
    if (routed) return routed
    if (mentions[key] === undefined) {
      const named = apps.filter((a) => {
        try {
          return JSON.stringify(a).includes('"' + key + '"')
        } catch (_) {
          return false
        }
      })
      mentions[key] = named.length === 1 ? named[0] : null
    }
    return mentions[key]
  }
  const byRoute = (route) =>
    apps.find((a) => Array.isArray(a.navbar) && a.navbar.some((n) => navRoute(n) === route)) || null
  // "tc_tasks" → "Tasks", "kz_merchants" → "Merchants", "leads" → "Leads"
  const collectionName = (key) => {
    const parts = key.split(/[_-]+/).filter(Boolean)
    const words = parts.length > 1 && parts[0].length <= 3 ? parts.slice(1) : parts
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }
  // WHO made it (Nika 2026-10-23: "it shows me 'Changed during the call' but
  // does not show me what" — the automations account's heartbeat, made by no
  // one in the call, read as the call's own change). A row names its actor:
  // only a call member's change counts (an agent acting for one carries that
  // member's e-mail); a background account or a system write is not the
  // call's. A row with no actor (older entries) still counts.
  const members = {}
  const addMember = (v) => {
    const k = keyOf(v)
    if (k.includes('@')) members[k] = true
  }
  addMember(r.currentUserEmail)
  for (const p of Array.isArray(r.livekitParticipants) ? r.livekitParticipants : []) {
    addMember(p && (p.email || p.name))
  }
  for (const u of Array.isArray(r.utterances) ? r.utterances : []) {
    if (u && u.roomId === r.activeRoomId) addMember(u.speakerEmail)
  }
  const hasMembers = Object.keys(members).length > 0
  // A member by name (the people list), else the address's local part — a
  // non-manager sees no peer e-mail there.
  const memberName = (email) => {
    const p = (Array.isArray(r.people) ? r.people : []).find((x) => x && keyOf(x.email) === email)
    return (p && p.name) || email.split('@')[0]
  }
  // "lastRunAt" → "Last run at", "tc_status" → "Tc status"
  const fieldLabel = (key) =>
    String(key)
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase())
  const groups = []
  const seen = {}
  const at = {}
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== 'object') continue
    const ev = String(row.eventType || '')
    if (ev !== 'created' && ev !== 'updated') continue
    const actor = row.actor && typeof row.actor === 'object' ? row.actor : null
    const actorEmail = keyOf((actor && actor.userEmail) || row.createdByEmail || '')
    if (actor && (actor.system === true || (hasMembers && !members[actorEmail]))) continue
    const type = String((row.entityRef && row.entityRef.type) || '')
    const recordId = String((row.entityRef && row.entityRef.id) || row.id || '')
    let app = null
    let route = ''
    let collection = ''
    if (type.startsWith('record:')) {
      collection = keyOf(type.slice(7))
      if (!collection) continue
      app = byCollection(collection)
      const rr =
        app &&
        Array.isArray(app.recordRoutes) &&
        app.recordRoutes.find(
          (x) => x && keyOf(x.key) === collection && typeof x.route === 'string'
        )
      route = (rr && rr.route) || ''
    } else if (/^crm/i.test(type)) {
      app = byRoute('/crm')
      route = '/crm'
      if (!app) continue
    } else {
      continue
    }
    const nav = app && Array.isArray(app.navbar) ? app.navbar.find((n) => navRoute(n)) : null
    const id = app ? String(app.id || app.key || app.appId || route) : 'collection-' + collection
    if (at[id] === undefined) {
      at[id] = groups.length
      seen[id] = { added: {}, updated: {}, who: {}, fields: {} }
      groups.push({
        key: 'changes-' + id.replace(/[^A-Za-z0-9_-]/g, '_'),
        label: app
          ? String(
              app.title ||
                app.name ||
                (nav && nav.label) ||
                collectionName(keyOf(id.split('/').pop()))
            )
          : collectionName(collection),
        // The app's RECORD key: the last segment of its `owner/key` id, the
        // key the App Store's copy hangs off — CallChanges.js ChangeApp names
        // the row through storeItemText with it. The whole id folded to a slug
        // no catalogue ships (`systemCommerceApp`), so the row printed the
        // manifest's "Store" where the App Store says "Commerce" (Nika
        // 2026-10-23: "app names = what a person sees in the workspace App
        // Store").
        appKey: app
          ? String(app.key || app.id || '')
              .split('/')
              .pop()
          : '',
        route: app ? route || (nav ? navRoute(nav) : '') : '',
        added: 0,
        updated: 0
      })
    }
    const marks = seen[id]
    const mark = recordId || type + ':' + groups[at[id]].added + groups[at[id]].updated
    if (ev === 'created') {
      marks.added[mark] = true
      delete marks.updated[mark]
    } else if (!marks.added[mark]) {
      marks.updated[mark] = true
    }
    const g = groups[at[id]]
    g.added = Object.keys(marks.added).length
    g.updated = Object.keys(marks.updated).length
    // WHAT: who changed it and which fields (the entry's own `changed` list).
    if (actorEmail) marks.who[actorEmail] = true
    const changed = row.payload && Array.isArray(row.payload.changed) ? row.payload.changed : []
    for (const f of changed) if (typeof f === 'string' && f) marks.fields[f] = true
    const who = Object.keys(marks.who).map(memberName)
    const fields = Object.keys(marks.fields).slice(0, 4).map(fieldLabel)
    if (who.length) g.who = who
    if (fields.length) g.fields = fields
  }
  return groups
}

// The parts' tick (MeetCallChanges, on the shell's 30 s `_meetingInviteTick`
// and when it mounts): read again when a minute has passed, while in a call.
export function meetCallChangesTick(el, s) {
  const host = el || this
  const root = (s && s.root) || (host.state && host.state.root)
  // in a call now (an old call's end screen, opened from the list, reads nothing)
  if (!root || !root.activeRoomId || root.livekitConnected !== true) return false
  const memo = root.__meetCallChanges || {}
  const cur = root._callChanges
  const fresh = cur && cur.roomId === root.activeRoomId
  if (memo.busy || (fresh && Date.now() - (memo.at || 0) < 60000)) return false
  host.call('meetCallChangesLoad', root.activeRoomId)
  return true
}

// What the parts show: the active room's groups, or [].
export function meetCallChangesOf(root) {
  const r = root && typeof root === 'object' ? root : {}
  const cur = r._callChanges
  if (!cur || !r.activeRoomId || cur.roomId !== r.activeRoomId) return []
  return Array.isArray(cur.groups) ? cur.groups : []
}
